"use client";

import type { ReactNode } from "react";
import { AlertOctagon, CheckCircle2, Eye, PenLine, Send, Sparkles, UserCheck } from "lucide-react";
import { cn } from "@/lib/cn";
import type { AiActionState, PrimitiveState } from "@/lib/types";
import { EmptyState, PermissionRestrictedState, ProviderDisconnectedState, RetryingState, SkeletonLine } from "./state-parts";

export interface AiAction {
  id: string;
  title: string;
  detail: string;
  state: AiActionState;
}

const STATE_META: Record<AiActionState, { label: string; icon: typeof Sparkles; tone: string }> = {
  suggested: { label: "Suggested", icon: Sparkles, tone: "text-white/90" },
  drafted: { label: "Drafted", icon: PenLine, tone: "text-white/90" },
  "sent-by-ai": { label: "Sent by AI", icon: Send, tone: "text-white/90" },
  "sent-by-human": { label: "Sent by human", icon: UserCheck, tone: "text-white/90" },
  "tool-action-confirmed": { label: "Tool action confirmed", icon: CheckCircle2, tone: "text-emerald-300" },
  failed: { label: "Failed", icon: AlertOctagon, tone: "text-rose-300" },
  "human-review-required": { label: "Human review required", icon: Eye, tone: "text-amber-300" },
};

export interface AiActionPanelProps {
  heading?: string;
  subheading?: string;
  actions: AiAction[];
  state?: Extract<
    PrimitiveState,
    "loading" | "empty" | "populated" | "permission-restricted" | "provider-disconnected" | "retrying"
  >;
  onSelectAction?: (action: AiAction) => void;
  footer?: ReactNode;
  className?: string;
}

/**
 * AI panel primitive — demonstrates the "AI Action State" vocabulary
 * verbatim from docs/COMPONENT_LIBRARY.md: suggested, drafted, sent by AI,
 * sent by human, tool action confirmed, failed, human review required.
 *
 * This is a presentational state-machine demo ONLY. Phase 0 has no real AI
 * behind it — clicking an action never calls a model or performs a real
 * side effect. Do not wire this to live data without an explicit later
 * phase; the badge in the header exists specifically to prevent this panel
 * from being mistaken for a working feature.
 */
export function AiActionPanel({
  heading = "AI Revenue Copilot",
  subheading = "Demo of the AI Action State model — not a live recommendation feed",
  actions,
  state = "populated",
  onSelectAction,
  footer,
  className,
}: AiActionPanelProps) {
  return (
    <div
      className={cn(
        "rounded-premium p-5 text-white shadow-[var(--shadow-premium)]",
        "bg-[linear-gradient(145deg,var(--color-brand-primary),var(--color-brand-deep))]",
        className,
      )}
      data-state={state}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-medium text-white">{heading}</h3>
          <p className="mt-0.5 text-xs text-white/70">{subheading}</p>
        </div>
        <span className="whitespace-nowrap rounded-pill bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
          Demo · not live
        </span>
      </div>

      {state === "loading" && (
        <div className="mt-4 space-y-3" aria-label="AI panel loading">
          <SkeletonLine className="h-10 bg-white/10" />
          <SkeletonLine className="h-10 bg-white/10" />
          <SkeletonLine className="h-10 bg-white/10" />
        </div>
      )}

      {state === "empty" && (
        <div className="mt-3 text-white [&_p]:text-white/70 [&_svg]:text-white">
          <EmptyState
            title="No suggestions right now"
            description="The copilot will surface actions here once service data is available."
          />
        </div>
      )}

      {state === "permission-restricted" && (
        <div className="mt-3 text-white [&_p]:text-white/70 [&_svg]:text-white">
          <PermissionRestrictedState description="Your role does not include AI recommendation access." />
        </div>
      )}

      {state === "provider-disconnected" && (
        <div className="mt-3 text-white [&_p]:text-white/70 [&_svg]:text-white">
          <ProviderDisconnectedState providerName="the AI recommendation service" />
        </div>
      )}

      {state === "retrying" && (
        <div className="mt-3 text-white [&_p]:text-white/70 [&_svg]:text-white">
          <RetryingState description="Reconnecting to the recommendation service" />
        </div>
      )}

      {state === "populated" && (
        <ul className="mt-3">
          {actions.map((action) => {
            const meta = STATE_META[action.state];
            const Icon = meta.icon;
            return (
              <li key={action.id} className="border-t border-white/12 py-3 first:mt-1 first:border-t-0">
                <button
                  type="button"
                  onClick={() => onSelectAction?.(action)}
                  className="flex w-full items-start justify-between gap-3 rounded-control text-start focus-visible:outline-2 focus-visible:outline-white"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-white">{action.title}</p>
                    <p className="mt-0.5 text-[11px] text-white/70">{action.detail}</p>
                    <span
                      className={cn(
                        "mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide",
                        meta.tone,
                      )}
                    >
                      <Icon aria-hidden="true" size={12} />
                      {meta.label}
                    </span>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {footer ? <div className="mt-3">{footer}</div> : null}
    </div>
  );
}
